import { ClientEvents } from 'common/events/ClientEvents';
import { StorageKeys } from 'common/storage/constants';
import { urlDateFormat } from 'common/UrlDate';
import { Maybe } from 'graphql/jsutils/Maybe';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { DELIVERY_DATE_TOOLTIP } from 'src/shared/Constants';
import { TrackedTooltip } from 'src/shared/design-system/Tooltip';
import { useOnMount } from 'src/shared/hooks/useOnMount';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import styled, { css } from 'styled-components';
import { DietaryTags } from 'web-common/src/shared/Enums';
import { Border, Colors, Font, ScreenWidth, Spacing, ZIndex } from 'web-common/src/shared/styles';
import {
  Date,
  Dates,
  DayOfWeek,
  DeliveryDayButton,
  MonthDay,
} from '../../explore/components/shef-filters/delivery-day/styles';
import { DietaryFilter } from '../../explore/components/shef-filters/dietary/DietaryFilter';
import {
  DateLabel,
  FiltersRowBackground,
  FiltersRowWrapper,
  TooltipText,
  TooltipTextWrapper,
} from '../../explore/components/shef-filters/styles';
import { IAvailabilityInfo } from './types';

const MenuFiltersRowBackground = styled(FiltersRowBackground)`
  margin-top: -16px;
`;

const MenuFiltersRowWrapper = styled(FiltersRowWrapper)`
  flex-wrap: wrap;
  padding-left: 15px;
  max-width: 100%;

  @media (max-width: ${ScreenWidth.SMALL}) {
    width: 100%;
    padding-left: ${Spacing.SINGLE};
  }
`;

const TopRow = styled(MenuFiltersRowWrapper)`
  padding-bottom: 0;
  position: relative;

  @media (max-width: ${ScreenWidth.SMALL}) {
    width: 100%;
    padding-left: ${Spacing.SINGLE};
    margin-right: auto;
    margin-left: auto;
  }
`;
const DatesRow = styled(MenuFiltersRowWrapper)`
  padding-top: 0%;
`;

const MenuDates = styled(Dates)`
  padding: 0;
`;

const MenuDeliveryDayButton = styled(DeliveryDayButton)`
  height: 58px;
  padding: ${Spacing.HALF};

  ${ifProp(
    'first',
    css`
      margin-left: 15px;
      @media (min-width: ${ScreenWidth.SMALL}) {
        margin-left: ${Spacing.NONE};
      }
    `
  )}
`;

const MenuTooltip = styled.div`
  background-color: ${Colors.RADISH};
  border-radius: ${Border.Radius.S};
  text-align: center;
  font-family: ${Font.Family.SANS};
  font-size: ${Font.Size.XXS};
  line-height: ${Font.LineHeight.XXS};
  font-weight: 600;
  color: ${Colors.WHITE};
  padding: ${Spacing.HALF} ${Spacing.THREE_QUARTERS};
  z-index: ${ZIndex.Tooltip};
`;

const Text = styled.span``;
const FloatingDietaryFilter = styled(DietaryFilter)`
  position: absolute;
  right: 0;
`;

interface IProps {
  availabilityInfo: IAvailabilityInfo[];
  currentDeliveryDateTime: DateTime;
  isPickupOrder: Maybe<boolean>;
  onDateChanged: (date: DateTime) => void;
  hasUrlDeliveryDate: boolean;
}

export const ProminentDeliveryDatePicker: React.FC<IProps> = ({
  availabilityInfo,
  currentDeliveryDateTime,
  isPickupOrder,
  onDateChanged,
  hasUrlDeliveryDate,
}) => {
  const [hasSeenTooltip, setHasSeenTooltip] = useLocalStorage(StorageKeys.DELIVERY_DATE_MENU_TOOLTIP, false);
  const handleDateChange = useCallback(
    (deliveryDateTime: DateTime) => {
      onDateChanged(deliveryDateTime);
      setHasSeenTooltip(hasSeenTooltip || !hasUrlDeliveryDate);
    },
    [onDateChanged, hasSeenTooltip, hasUrlDeliveryDate, setHasSeenTooltip]
  );
  const ref = useRef<HTMLButtonElement>(null);
  useOnMount(() => {
    if (ref.current) {
      ref.current.scrollIntoView(false);
    }
  });

  const deliveryDays = useMemo(
    () =>
      availabilityInfo.map(({ dateTime, isSoldOut }, idx) => {
        const deliveryDayString = dateTime.toFormat(urlDateFormat);
        const isSelected = dateTime.equals(currentDeliveryDateTime);
        const button = (
          <MenuDeliveryDayButton
            ref={isSelected ? ref : undefined}
            first={idx === 0}
            key={deliveryDayString}
            onClick={() => handleDateChange(dateTime)}
            isSelected={isSelected}
            isSoldOut={isSoldOut}>
            <Date>
              <DayOfWeek>{isSoldOut ? 'Sold out' : dateTime.weekdayShort}</DayOfWeek>
              <MonthDay>
                {dateTime.monthShort} {dateTime.day}
              </MonthDay>
            </Date>
          </MenuDeliveryDayButton>
        );

        return idx === 0 && dateTime.equals(currentDeliveryDateTime) ? (
          <Popover
            key={deliveryDayString}
            reposition={false}
            isOpen={!hasSeenTooltip && !hasUrlDeliveryDate}
            positions={['bottom']}
            padding={4}
            content={({ position, childRect, popoverRect }) => (
              <ArrowContainer
                style={{ marginLeft: childRect.left - popoverRect.left - 4 }}
                position={position}
                childRect={childRect}
                popoverRect={popoverRect}
                arrowColor={Colors.RADISH}
                arrowSize={4}>
                <MenuTooltip>First available date</MenuTooltip>
              </ArrowContainer>
            )}
            containerStyle={{ zIndex: `${ZIndex.DeliveryDayPopover}` }}>
            {button}
          </Popover>
        ) : (
          button
        );
      }),
    [availabilityInfo, currentDeliveryDateTime, handleDateChange, hasSeenTooltip, hasUrlDeliveryDate]
  );

  const [isDietaryFilterOpen, setDietaryFilterOpen] = useState(false);
  const toggleDietaryFilter = useCallback(() => {
    setDietaryFilterOpen((isOpen) => !isOpen);
  }, []);
  const [dietaryPrefs, setDietaryPrefs] = useLocalStorage<DietaryTags[]>(StorageKeys.DIETARY_PREFERENCES, []);
  const handleDietaryPrefs = useCallback(
    ({ dietaryTags }: { dietaryTags?: DietaryTags[] }) => {
      setDietaryPrefs(dietaryTags ?? []);
      setDietaryFilterOpen(false);
    },
    [setDietaryPrefs]
  );

  if (availabilityInfo.filter(({ isSoldOut }) => !isSoldOut).length === 0) {
    return null;
  }

  return (
    <MenuFiltersRowBackground offsetTop={0}>
      <TopRow>
        <Text>
          <DateLabel data-cy='pick-a-delivery-data-label'>
            Pick a {isPickupOrder ? 'pick up' : 'delivery'} date
          </DateLabel>
          <TooltipTextWrapper>
            <TrackedTooltip
              showInfoCircle
              event={ClientEvents.DELIVERY_DATE_TOOLTIP_OPEN}
              eventProperty={{ tooltipPage: 'menu' }}
              closeOnClickMobile={true}
              content={DELIVERY_DATE_TOOLTIP}>
              <TooltipText>Why?</TooltipText>
            </TrackedTooltip>
          </TooltipTextWrapper>
        </Text>
        <FloatingDietaryFilter
          isOpen={isDietaryFilterOpen}
          onOpen={toggleDietaryFilter}
          selectedDietaryFilters={dietaryPrefs}
          onChange={handleDietaryPrefs}
        />
      </TopRow>
      <DatesRow>
        <MenuDates>{deliveryDays}</MenuDates>
      </DatesRow>
    </MenuFiltersRowBackground>
  );
};
